<template>
  <div data-test="investment-mix-donut" class="flex flex-col items-center gap-3">
    <div v-if="!shares" class="text-sm" style="color: var(--fg-muted)">
      No M4 ratio for this sprint
    </div>
    <template v-else>
      <div class="relative h-44 w-44">
        <Doughnut :data="chartData" :options="chartOptions" />
        <div class="pointer-events-none absolute inset-0 flex flex-col items-center justify-center">
          <span class="text-[11px] uppercase tracking-wider" style="color: var(--fg-subtle)">
            F7
          </span>
          <span class="text-2xl font-semibold leading-tight" style="color: var(--fg)">
            {{ f7Label }}
          </span>
        </div>
      </div>
      <div
        class="inline-flex items-center gap-1.5 rounded-md px-2 py-0.5 text-xs"
        :style="{
          background: 'var(--bg-card)',
          border: `1px solid var(${verdictBorderVar})`,
          color: `var(${verdictColorVar})`,
        }"
        :data-test-verdict="verdict"
      >
        <span class="size-1.5 rounded-full" :style="{ background: `var(${verdictColorVar})` }" />
        {{ verdictLabel }}
      </div>
    </template>
  </div>
</template>

<script setup lang="ts">
import "@/lib/chartSetup";
import { computed } from "vue";
import { Doughnut } from "vue-chartjs";
import type { ChartData, ChartOptions } from "chart.js";
import { cssVar } from "@/lib/chartTokens";
import {
  PROGRAMME_TARGET,
  mixOnTarget,
  type MixShares,
  type TargetVerdict,
} from "@/lib/investmentMix";

const props = withDefaults(
  defineProps<{
    shares: MixShares | null;
    target?: MixShares;
  }>(),
  { target: () => PROGRAMME_TARGET },
);

const verdict = computed<TargetVerdict>(() => mixOnTarget(props.shares, props.target));

const verdictLabel = computed(() => {
  if (verdict.value === "on") return "On target (60/30/10)";
  if (verdict.value === "off") return "Off target";
  return "—";
});

const verdictColorVar = computed(() => {
  if (verdict.value === "on") return "--rag-green";
  if (verdict.value === "off") return "--rag-amber";
  return "--fg-muted";
});

const verdictBorderVar = computed(() => {
  if (verdict.value === "on") return "--rag-green";
  if (verdict.value === "off") return "--rag-amber";
  return "--border";
});

const f7Label = computed(() => {
  if (!props.shares) return "—";
  return `${Math.round(props.shares.f7 * 100)}%`;
});

// Brand-ish palette: F7 = accent, BAU = amber, Stability = green.
// All read at compute-time so the donut updates on theme change re-render.
const chartData = computed<ChartData<"doughnut", number[], string>>(() => {
  const s = props.shares ?? { f7: 0, bau: 0, stability: 0 };
  return {
    labels: ["F7", "BAU", "Stability"],
    datasets: [
      {
        data: [s.f7, s.bau, s.stability],
        backgroundColor: [
          cssVar("--accent"),
          cssVar("--rag-amber"),
          cssVar("--rag-green"),
        ],
        borderColor: cssVar("--bg-elev"),
        borderWidth: 2,
      },
    ],
  };
});

const chartOptions = computed<ChartOptions<"doughnut">>(() => ({
  responsive: true,
  maintainAspectRatio: false,
  cutout: "65%",
  plugins: {
    legend: { display: false },
    tooltip: {
      callbacks: {
        label: (ctx) => {
          const v = (ctx.parsed as unknown as number) ?? 0;
          return `${ctx.label}: ${(v * 100).toFixed(1)}%`;
        },
      },
    },
  },
}));

defineExpose({ chartData, chartOptions, verdict });
</script>
